import { useState, useEffect, useLayoutEffect, memo, useMemo, useCallback, useRef } from 'react';
import { EDSTextField } from '@gehc-eds-react/eds-text-field';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../redux/hooks';
import { addErrorNotification, addSuccessNotification } from '../../redux/slices/notificationSlice';
import { apiFetch } from '../../utils/apiClient';
import SpinnerComponent from '../../components/SpinnerComponent/SpinnerComponent';
import { fetchAccountsByContractId } from '../../services/accountsService';
import { EditSourceModal, DeleteSourceModal } from './SourceModel';
import TestConnectionModal from './TestConnectionModel';
import SourceListTable from './SourceListTable';

const ACCOUNTS_PAGE_SIZE = 10;

const PrefilledField = ({ name, label, value }) => (
    <div className="u-mt onboarding-prefilled-field">
        <label htmlFor={name} style={{ display: 'block', marginBottom: '6px', color: 'var(--eds-text-icon-default)' }}>
            {label}
        </label>
        <input
            id={name}
            name={name}
            type="text"
            value={value || ''}
            readOnly
            style={{ width: '100%', height: '36px', padding: '0 12px', border: '1px solid transparent', borderRadius: '4px', backgroundColor: '#f5f6f7', color: '#8a8f98' }}
        />
    </div>
);

const CreateS3Upload = ({ onClose, selectedContract }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // ── Register Source form state ──
    const [accountName, setAccountName] = useState('');
    const [bucketName, setBucketName] = useState('');
    const [prefix, setPrefix] = useState('');
    const [existingDatasetName, setExistingDatasetName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [accountNameError, setAccountNameError] = useState('');
    const [bucketNameError, setBucketNameError] = useState('');
    const [prefixError, setPrefixError] = useState('');
    const [isBucketInfoVisible, setIsBucketInfoVisible] = useState(false);

    // ── Source list state (accounts, sorting, search, pagination) ──
    const [sourceAccounts, setSourceAccounts] = useState([]);
    const [accountsPagination, setAccountsPagination] = useState({
        page: 1,
        pageSize: ACCOUNTS_PAGE_SIZE,
        totalPages: 0,
        totalCount: 0,
        count: 0
    });
    const [isAccountsLoading, setIsAccountsLoading] = useState(false);
    const [accountsError, setAccountsError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
    const [sourceSearchTerm, setSourceSearchTerm] = useState('');
    const sourceListScrollRef = useRef(null);
    const firstMatchedRowRef = useRef(null);

    // ── Delete Source modal state ──
    const [sourceToDelete, setSourceToDelete] = useState(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isDeletingSource, setIsDeletingSource] = useState(false);

    // ── Edit Source modal state ──
    const [sourceToEdit, setSourceToEdit] = useState(null);
    const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
    const [editPrefix, setEditPrefix] = useState('');
    const [editDisplayName, setEditDisplayName] = useState('');
    const [isUpdatingSource, setIsUpdatingSource] = useState(false);

    // ── Test Connection state ──
    const [isTestConnectionRunning, setIsTestConnectionRunning] = useState(false);
    const [testConnectionModal, setTestConnectionModal] = useState({ isOpen: false, account: null, result: null, error: null });
    const [expandedCheckDetails, setExpandedCheckDetails] = useState({});

    const [prefilledData, setPrefilledData] = useState({
        dataPartner: '', businessUnit: '', project: '', contract: '', contractId: ''
    });

    useEffect(() => {
        if (selectedContract) {
            setPrefilledData({
                dataPartner: selectedContract.DataPartnerDisplayName || selectedContract.DataPartnerName || '',
                businessUnit: selectedContract.BusinessUnit || '',
                project: selectedContract.Project || '',
                contract: selectedContract.ContractDisplayName || selectedContract.Name || '',
                contractId: selectedContract.ContractID || ''
            });
        }
    }, [selectedContract]);

    const selectedContractId = useMemo(
        () => selectedContract?.ContractID || selectedContract?.contract_id || '',
        [selectedContract]
    );

    // ── Load / paginate source accounts ──

    const loadAccountsPage = useCallback(async (page) => {
        if (!selectedContractId) {
            setSourceAccounts([]);
            setAccountsPagination(prev => ({ ...prev, page: 1, totalPages: 0, totalCount: 0, count: 0 }));
            return;
        }

        setIsAccountsLoading(true);
        setAccountsError('');

        try {
            const result = await fetchAccountsByContractId(selectedContractId, page, ACCOUNTS_PAGE_SIZE);
            setSourceAccounts(result.accounts);
            setAccountsPagination({
                page: result.page, pageSize: result.pageSize, totalPages: result.totalPages,
                totalCount: result.totalCount, count: result.count
            });
        } catch (error) {
            console.error('Failed to fetch source accounts:', error);
            setSourceAccounts([]);
            setAccountsError(error.message || 'Failed to load source accounts.');
            dispatch(addErrorNotification({ message: error.message || 'Failed to load source accounts. Please try again.' }));
        } finally {
            setIsAccountsLoading(false);
        }
    }, [dispatch, selectedContractId]);

    useEffect(() => {
        loadAccountsPage(1);
    }, [loadAccountsPage]);

    // ── Sorting / searching the loaded page of accounts ──

    const getSortValue = (account, key) => {
        if (key === 'account_display_name') {
            return `${account.account_id || ''} ${account.display_name || ''}`.trim().toLowerCase();
        }
        return (account[key] || '').toString().toLowerCase();
    };

    const sortedSourceAccounts = useMemo(() => {
        if (!sortConfig.key) return sourceAccounts;
        return [...sourceAccounts].sort((a, b) => {
            const left = getSortValue(a, sortConfig.key);
            const right = getSortValue(b, sortConfig.key);
            if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
            if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [sourceAccounts, sortConfig]);

    const getSearchRank = useCallback((account, search) => {
        const searchableText = [account.account_id, account.display_name, account.source_bucket, account.replicate_prefix]
            .filter(Boolean).join(' ').toLowerCase();
        if (searchableText === search) return 0;
        if (searchableText.startsWith(search)) return 1;
        if (searchableText.includes(search)) return 2;
        return 3;
    }, []);

    const filteredSourceAccounts = useMemo(() => {
        const search = sourceSearchTerm.trim().toLowerCase();
        if (!search) return sortedSourceAccounts;

        return [...sortedSourceAccounts]
            .filter((account) => {
                const searchableText = [account.account_id, account.display_name, account.source_bucket, account.replicate_prefix]
                    .filter(Boolean).join(' ').toLowerCase();
                return searchableText.includes(search);
            })
            .sort((a, b) => {
                const rankDiff = getSearchRank(a, search) - getSearchRank(b, search);
                if (rankDiff !== 0) return rankDiff;
                const left = getSortValue(a, sortConfig.key);
                const right = getSortValue(b, sortConfig.key);
                if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
                if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
    }, [getSearchRank, sortConfig.direction, sortConfig.key, sortedSourceAccounts, sourceSearchTerm]);

    useLayoutEffect(() => {
        if (sourceListScrollRef.current) {
            sourceListScrollRef.current.scrollTop = 0;
        }
    }, [filteredSourceAccounts, sourceSearchTerm]);

    const handleSort = (key) => {
        setSortConfig((prev) => (
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        ));
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return ' ↕';
        return sortConfig.direction === 'asc' ? ' ^' : ' v';
    };

    const handleSourceSearchInput = useCallback((event) => {
        setSourceSearchTerm(event.detail?.searchTerm ?? event.detail?.value ?? event.detail ?? '');
    }, []);

    const handleSourceSearchClear = useCallback(() => setSourceSearchTerm(''), []);

    // ── Register Source (onboarding) submit ──

    const validateAccountNumber = (account) => {
        if (!account || !account.trim()) return 'Account number is required.';
        if (!/^\d{12}$/.test(account.trim())) return 'Account number must be 12 digits.';
        return '';
    };

    const resetFormState = () => {
        setAccountName('');
        setBucketName('');
        setPrefix('');
        setExistingDatasetName('');
        setAccountNameError('');
        setBucketNameError('');
        setPrefixError('');
    };

    const getValidationMessages = (error) => {
        const details = Array.isArray(error?.detail) ? error.detail : [];
        if (details.length > 0) {
            return details.map((item) => {
                const location = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : '';
                const message = item?.msg || '';
                const labelMap = { source_bucket: 'Bucket Name', display_name: 'Display Name', account_id: 'Account Number', replicate_prefix: 'Prefix' };
                const fieldLabel = labelMap[location] || '';
                if (!message) return '';
                return fieldLabel ? `${fieldLabel}: ${message}` : message;
            }).filter(Boolean);
        }
        return error?.message ? [error.message] : [];
    };

    const applyBackendFieldErrors = (error) => {
        const details = Array.isArray(error?.detail) ? error.detail : [];
        let hasFieldError = false;

        if (details.length > 0) {
            details.forEach((item) => {
                const location = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : '';
                const message = item?.msg || '';
                if (!message) return;
                if (location === 'source_bucket') { setBucketNameError(message); hasFieldError = true; }
                if (location === 'account_id') { setAccountNameError(message); hasFieldError = true; }
                if (location === 'replicate_prefix') { setPrefixError(message); hasFieldError = true; }
            });
            return hasFieldError;
        }

        const backendMessage = (error?.message || '').trim();
        const normalizedMessage = backendMessage.toLowerCase();
        if (normalizedMessage.includes('bucket')) { setBucketNameError(backendMessage); return true; }
        if (normalizedMessage.includes('account')) { setAccountNameError(backendMessage); return true; }
        if (normalizedMessage.includes('prefix')) { setPrefixError(backendMessage); return true; }
        return false;
    };

    const handleSubmit = async () => {
        const accountErr = validateAccountNumber(accountName);
        const bucketErr = !bucketName.trim() ? 'Bucket name is required.' : '';
        setAccountNameError(accountErr);
        setBucketNameError(bucketErr);
        if (accountErr || bucketErr) return;

        setIsSubmitting(true);
        try {
            let finalPrefix = prefix.trim() || '/';
            if (finalPrefix !== '/' && !finalPrefix.endsWith('/')) finalPrefix += '/';

            const payload = {
                account_id: accountName.trim(),
                replicate_prefix: finalPrefix,
                source_bucket: bucketName.trim(),
                contract_id: prefilledData.contractId,
                display_name: existingDatasetName.trim()
            };

            await apiFetch('/api/v1/s3-batch/sources/onboard', {
                method: 'POST', body: JSON.stringify(payload), contentType: 'application/json', retry: false
            });

            dispatch(addSuccessNotification({ message: `S3 source onboarding for "${bucketName.trim()}" initiated successfully.` }));
            resetFormState();
            await loadAccountsPage(1);
        } catch (error) {
            console.error('S3 onboarding error:', error);
            applyBackendFieldErrors(error);
            const validationMessages = getValidationMessages(error);
            dispatch(addErrorNotification({
                message: validationMessages.length > 0 ? validationMessages.join(' | ') : 'Failed to onboard S3 source. Please try again.'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => onClose();

    // ── Edit Source ──

    const handleEditAccount = (account) => {
        setSourceToEdit(account);
        setEditPrefix((account.replicate_prefix || '').replace(/\/$/, ''));
        setEditDisplayName(account.display_name || '');
        setIsEditPopupOpen(true);
        setAccountNameError('');
        setBucketNameError('');
        setPrefixError('');
    };

    const closeEditPopup = () => {
        if (isUpdatingSource) return;
        setIsEditPopupOpen(false);
        setSourceToEdit(null);
    };

    const saveEditAccount = async () => {
        const trimmedPrefix = editPrefix.trim();
        const normalizedPrefix = trimmedPrefix && !trimmedPrefix.endsWith('/') ? `${trimmedPrefix}/` : trimmedPrefix;
        const sourceId = sourceToEdit?.source_id || sourceToEdit?.sourceId || sourceToEdit?.id;
        const contractId = selectedContractId || sourceToEdit?.contract_id || prefilledData.contractId;

        if (!sourceId) {
            dispatch(addErrorNotification({ message: 'Update failed: source id is missing for this account.' }));
            return;
        }
        if (!contractId) {
            dispatch(addErrorNotification({ message: 'Update failed: contract id is missing for this account.' }));
            return;
        }

        setIsUpdatingSource(true);
        try {
            await apiFetch(`/api/v1/s3-batch/sources/${sourceId}`, {
                method: 'PUT',
                body: JSON.stringify({
                    contract_id: contractId,
                    display_name: editDisplayName.trim(),
                    replicate_prefix: normalizedPrefix || '/'
                })
            });

            dispatch(addSuccessNotification({ message: `Updated source ${sourceToEdit?.account_id || sourceId} successfully.` }));
            setIsEditPopupOpen(false);
            setSourceToEdit(null);
            await loadAccountsPage(accountsPagination.page);
        } catch (error) {
            console.error('Failed to update source:', error);
            dispatch(addErrorNotification({ message: error.message || 'Failed to update source. Please try again.' }));
        } finally {
            setIsUpdatingSource(false);
        }
    };

    // ── Delete Source ──

    const handleDeleteAccount = (account) => {
        setSourceToDelete(account);
        setIsDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = () => {
        if (isDeletingSource) return;
        setIsDeleteConfirmOpen(false);
        setSourceToDelete(null);
    };

    const confirmDeleteAccount = async () => {
        const sourceId = sourceToDelete?.source_id || sourceToDelete?.sourceId || sourceToDelete?.id;
        if (!sourceId) {
            dispatch(addErrorNotification({ message: 'Deboard failed: source id is missing for this account.' }));
            closeDeleteConfirm();
            return;
        }

        setIsDeletingSource(true);
        try {
            await apiFetch(`/api/v1/s3-batch/sources/${sourceId}`, { method: 'DELETE' });
            dispatch(addSuccessNotification({ message: `Deboarded source ${sourceToDelete?.account_id || sourceId} successfully.` }));
            closeDeleteConfirm();
            await loadAccountsPage(accountsPagination.page);
        } catch (error) {
            console.error('Failed to deboard source:', error);
            dispatch(addErrorNotification({ message: error.message || 'Failed to deboard source. Please try again.' }));
        } finally {
            setIsDeletingSource(false);
        }
    };

    // ── Test Connection ──

    const getAccountSourceId = (account) => account?.source_id || account?.sourceId || account?.id;

    const runTestConnection = async (account) => {
        const sourceId = getAccountSourceId(account);
        if (!sourceId) {
            dispatch(addErrorNotification({ message: 'Test connection failed: source id is missing for this account.' }));
            return;
        }

        setExpandedCheckDetails({});
        setIsTestConnectionRunning(true);

        try {
            const response = await apiFetch(`/api/v1/s3-batch/sources/${sourceId}/test-connection`, { method: 'POST', retry: false });
            setTestConnectionModal({ isOpen: true, account, result: response, error: null });
            await loadAccountsPage(accountsPagination.page);
        } catch (error) {
            console.error('Failed to test connection:', error);
            setTestConnectionModal({ isOpen: true, account, result: null, error: error.message || 'Failed to test connection. Please try again.' });
            dispatch(addErrorNotification({ message: error.message || 'Failed to test connection. Please try again.' }));
        } finally {
            setIsTestConnectionRunning(false);
        }
    };

    const handleTestConnection = (account) => runTestConnection(account);

    const handleRunTestConnectionAgain = () => {
        if (testConnectionModal.account) runTestConnection(testConnectionModal.account);
    };

    const closeTestConnectionModal = () => {
        if (isTestConnectionRunning) return;
        setTestConnectionModal({ isOpen: false, account: null, result: null, error: null });
    };

    const toggleCheckDetails = (checkName) => {
        setExpandedCheckDetails((prev) => ({ ...prev, [checkName]: !prev[checkName] }));
    };

    // ── Navigation ──

    const handleOpenSourceDetails = (account) => {
        const sourceId = account?.source_id || account?.sourceId || account?.id || account?.account_id || 'source';
        navigate(`/s3-upload/source/${encodeURIComponent(sourceId)}`, { state: { source: account, selectedContract } });
    };

    return (
        <><div className="data-upload-container" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            <SpinnerComponent isVisible={isSubmitting} overlay={true} message="Registering Source..." />
            <SpinnerComponent isVisible={isTestConnectionRunning} overlay={true} message="Testing connection..." />

            <div className="upload-data-header fill--1">
                <h1 className="u-mt0 u-mb0">S3 to S3 Upload</h1>
            </div>

            <div className="flex user-groups-container" style={{ flex: '1 1 auto', minHeight: 0 }}>
                {/* ── Left panel: Register Source form ── */}
                <div className="user-details-section u--1/4 fill--1" style={{ paddingBottom: '24px' }}>
                    <div><h4 className="u-mt0 u-mb--">Register Source</h4></div>

                    <div className="u-mt">
                        <EDSTextField
                            name="account-number"
                            label={{ width: '', position: 'top', text: 'Account Number' }}
                            placeholder="Enter 12-digit account number"
                            isRequired={true}
                            validationError={accountNameError}
                            width="100%"
                            type="text"
                            value={accountName}
                            maxLength={12}
                            onEDSTextFieldChange={(e) => {
                                const inputValue = e.target.value;
                                const numericValue = inputValue.replace(/[^\d]/g, '');
                                const limitedValue = numericValue.slice(0, 12);
                                setAccountName(limitedValue);
                                if (inputValue !== numericValue) {
                                    setAccountNameError('Account number must contain only digits.');
                                } else if (limitedValue.length === 12 && inputValue.length > 12) {
                                    setAccountNameError('Account number must be exactly 12 digits.');
                                } else if (limitedValue.length > 0 && limitedValue.length < 12) {
                                    setAccountNameError(`${12 - limitedValue.length} more digit(s) required.`);
                                } else {
                                    setAccountNameError('');
                                }
                            }}
                            isDisabled={isSubmitting} />
                        {accountNameError && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>{accountNameError}</div>}
                    </div>

                    <div className="u-mt">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginBottom: '6px',
                                position: 'relative'
                            }}
                            onMouseLeave={() => setIsBucketInfoVisible(false)}
                        >
                            <span style={{ color: 'var(--eds-text-icon-default)' }}>Bucket Name</span>
                            <span
                                aria-hidden="true"
                                style={{ color: '#d32f2f', fontSize: '14px', lineHeight: 1, fontWeight: 700 }}
                            >
                                *
                            </span>
                            <button
                                type="button"
                                aria-label="Bucket Name format info"
                                onMouseEnter={() => setIsBucketInfoVisible(true)}
                                onFocus={() => setIsBucketInfoVisible(true)}
                                onBlur={() => setIsBucketInfoVisible(false)}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    border: '1px solid #9e9e9e',
                                    background: '#fff',
                                    color: '#616161',
                                    fontSize: '12px',
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    cursor: 'help',
                                    padding: 0
                                }}
                            >
                                i
                            </button>
                            {isBucketInfoVisible && (
                                <div
                                    role="tooltip"
                                    style={{
                                        position: 'absolute',
                                        left: '26px',
                                        top: '-4px',
                                        zIndex: 5,
                                        maxWidth: '300px',
                                        backgroundColor: '#1f2937',
                                        color: '#fff',
                                        fontSize: '12px',
                                        lineHeight: 1.4,
                                        padding: '8px 10px',
                                        borderRadius: '6px',
                                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
                                    }}
                                >
                                    S3 bucket name must be 3-63 characters, lowercase letters, numbers, dots, and hyphens only.
                                </div>
                            )}
                        </div>
                        <EDSTextField
                            name="bucket-name"
                            label={{ width: '', position: 'top', text: '' }}
                            aria-label="Bucket Name"
                            placeholder="Enter bucket name"
                            isRequired={true}
                            validationError={bucketNameError}
                            width="100%"
                            type="text"
                            value={bucketName}
                            onEDSTextFieldChange={(e) => { setBucketName(e.target.value); if (bucketNameError) setBucketNameError(''); }}
                            isDisabled={isSubmitting} />
                        {bucketNameError && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>{bucketNameError}</div>}
                    </div>

                    <div className="u-mt">
                        <EDSTextField
                            name="prefix"
                            label={{ width: '', position: 'top', text: 'Prefix' }}
                            placeholder='Enter prefix (optional, defaults to slash("/"))'
                            isRequired={false}
                            validationError={prefixError}
                            width="100%"
                            type="text"
                            value={prefix}
                            onEDSTextFieldChange={(e) => { setPrefix(e.target.value); if (prefixError) setPrefixError(''); }}
                            isDisabled={isSubmitting} />
                        {prefixError && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>{prefixError}</div>}
                    </div>

                    <div className="u-mt">
                        <EDSTextField
                            name="display-name"
                            label={{ width: '', position: 'top', text: 'Display Name' }}
                            placeholder="Select or enter display name (optional)"
                            isRequired={false}
                            width="100%"
                            type="text"
                            value={existingDatasetName}
                            onEDSTextFieldChange={(e) => setExistingDatasetName(e.target.value)}
                            isDisabled={isSubmitting} />
                    </div>

                    <PrefilledField name="contract" label="Contract" value={prefilledData.contract} />
                    <PrefilledField name="business-unit" label="Business Unit" value={prefilledData.businessUnit} />
                    <PrefilledField name="project" label="Project" value={prefilledData.project} />
                    <PrefilledField name="data-partner" label="Data Partner" value={prefilledData.dataPartner} />
                </div>

                <SourceListTable
                    selectedContractId={selectedContractId}
                    sourceSearchTerm={sourceSearchTerm}
                    onSourceSearchInput={handleSourceSearchInput}
                    onSourceSearchClear={handleSourceSearchClear}
                    isAccountsLoading={isAccountsLoading}
                    accountsError={accountsError}
                    sourceAccounts={sourceAccounts}
                    filteredSourceAccounts={filteredSourceAccounts}
                    onSort={handleSort}
                    getSortIndicator={getSortIndicator}
                    scrollRef={sourceListScrollRef}
                    firstMatchedRowRef={firstMatchedRowRef}
                    onOpenSourceDetails={handleOpenSourceDetails}
                    onEditAccount={handleEditAccount}
                    onDeleteAccount={handleDeleteAccount}
                    onTestConnection={handleTestConnection}
                    isTestConnectionRunning={isTestConnectionRunning}
                    accountsPagination={accountsPagination}
                    onPreviousPage={() => loadAccountsPage(accountsPagination.page - 1)}
                    onNextPage={() => loadAccountsPage(accountsPagination.page + 1)} />
            </div>

        </div>
            <EditSourceModal
                isOpen={isEditPopupOpen && !!sourceToEdit}
                editPrefix={editPrefix}
                setEditPrefix={setEditPrefix}
                editDisplayName={editDisplayName}
                setEditDisplayName={setEditDisplayName}
                isUpdatingSource={isUpdatingSource}
                onClose={closeEditPopup}
                onSave={saveEditAccount} />
            <DeleteSourceModal
                isOpen={isDeleteConfirmOpen}
                sourceToDelete={sourceToDelete}
                isDeletingSource={isDeletingSource}
                onClose={closeDeleteConfirm}
                onConfirm={confirmDeleteAccount} />
            <TestConnectionModal
                modalState={testConnectionModal}
                isTestConnectionRunning={isTestConnectionRunning}
                expandedCheckDetails={expandedCheckDetails}
                onToggleCheckDetails={toggleCheckDetails}
                onRunAgain={handleRunTestConnectionAgain}
                onClose={closeTestConnectionModal} /><div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', padding: '15px 24px', minHeight: '48px', borderTop: '1px solid #e0e0e0', backgroundColor: 'var(--eds-card-fill-color)', alignItems: 'center', position: 'sticky', bottom: 0, zIndex: 2 }}>
                <EDSButton
                    variant="primary" type="button" label={isSubmitting ? 'Submitting...' : 'Register'}
                    icon={{ name: 'ico-upload-16', position: 'left' }}
                    onEDSButtonClick={handleSubmit}
                    isDisabled={isSubmitting}
                    data-testid="btn-Submit" />
                <EDSButton variant="secondary" type="button" label="Back To Contracts" onEDSButtonClick={handleCancel} isDisabled={isSubmitting} />
            </div></>
    );
};

export default memo(CreateS3Upload);